import { useCallback, useState } from "react";
import { DateTime } from "luxon";

import { SpendFilters } from "lib/hooks/api/spend/spendFilters";
import { EventNames, TrackingEvent, TrackingProvider, useTracking } from "lib/tracking";
import { TrendRow } from "./spendChartRows";
import SpendTrendChart from "./SpendTrendChart";
import { TransactionsModal } from "./TransactionsModal";

type Props = {
  /** The page's filters; a clicked period narrows them to open its transactions. */
  filters: SpendFilters;
  dateUnit: "month" | "quarter" | "year";
  rows: TrendRow[];
  isLoading: boolean;
};

function createChartClickedOnEvent(filterValue: string): TrackingEvent {
  return {
    name: EventNames.chartClickedOn,
    data: {
      "Action type": "Data Inspected",
      "Context source": "All Transactions Chart",
      "Filter value": filterValue,
    },
  };
}

/** Spend per period, from rows the view has already built. Click a period to see its transactions. */
export function TransactionChart({ filters, dateUnit, rows, isLoading }: Props) {
  const [modalFilters, setModalFilters] = useState<SpendFilters | null>(null);
  const { logEvents } = useTracking();

  const onPeriodClick = useCallback(
    (periodStart: string) => {
      const period = DateTime.fromISO(periodStart);

      logEvents(createChartClickedOnEvent(periodStart));

      setModalFilters({
        ...filters,
        dateFrom: periodStart,
        dateTo: period.endOf(dateUnit).toISODate() ?? undefined,
      });
    },
    [dateUnit, filters, logEvents],
  );

  return (
    <TrackingProvider data={{ "Context source": "All Transactions" }}>
      <SpendTrendChart rows={rows} isLoading={isLoading} onPeriodClick={onPeriodClick} />
      {modalFilters && (
        <TransactionsModal isOpen onClose={() => setModalFilters(null)} filters={modalFilters} />
      )}
    </TrackingProvider>
  );
}
